import { motion } from "framer-motion";
import { Crown, CheckCircle, Phone, ArrowRight, Heart, Sparkle } from "@phosphor-icons/react";
import { EXECUTIVE, PHONE, PHONE_RAW } from "../data";

const POEM = [
  "Dim the worries, let the evening glow —",
  "we'll drive, you'll dazzle, steal the show.",
  "No keys, no parking, just the night ahead;",
  "tonight is yours — leave the road to us instead.",
];

export const ExecutiveTier = () => (
  <section id="executive" data-testid="executive-section" className="relative py-20 sm:py-28 bg-ink text-white overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
      {/* Copy */}
      <div>
        <span className="inline-flex items-center gap-2 bg-sun text-ink text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 mb-5">
          <Crown weight="fill" size={14} /> Premium · DS Executive
        </span>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          Travel first-class in our <span className="text-sun">executive van.</span>
        </h2>
        <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
          Our top-tier ride for those who want extra comfort and a flawless experience — a
          spacious, immaculate {EXECUTIVE.vehicle.toLowerCase()}, always on time.
        </p>

        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {EXECUTIVE.features.map((f, i) => (
            <motion.li
              key={f}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`exec-feature-${i}`}
              className="flex items-center gap-3 text-white/90"
            >
              <CheckCircle weight="fill" size={20} className="text-sun shrink-0" />
              <span className="text-sm">{f}</span>
            </motion.li>
          ))}
        </ul>

        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 mb-2">Executive extras</p>
          <div className="flex flex-wrap gap-2">
            {EXECUTIVE.perks.map((p) => (
              <span
                key={p}
                data-testid={`exec-perk-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/85 bg-white/[0.06] border border-white/15 px-3 py-1.5 hover:border-sun/60 transition-colors"
              >
                <Sparkle weight="fill" size={12} className="text-sun" /> {p}
              </span>
            ))}
          </div>
        </div>

        {/* Date night poem */}
        <div data-testid="datenight-poem" className="mt-9 border-l-4 border-sun pl-5 py-1">
          <p className="inline-flex items-center gap-2 text-sun text-xs font-bold uppercase tracking-[0.2em] mb-3">
            <Heart weight="fill" size={14} /> Perfect for date night
          </p>
          <div className="font-display text-lg sm:text-xl leading-relaxed text-white/85 italic">
            {POEM.map((line) => (<span key={line} className="block">{line}</span>))}
          </div>
        </div>

        <div className="mt-9 flex flex-wrap gap-4">
          <button
            data-testid="exec-book-btn"
            onClick={() => document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" })}
            className="flex items-center gap-2.5 bg-sun text-ink font-bold text-base px-7 py-4 border-2 border-sun shadow-hard hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all duration-150"
          >
            Book the Executive ride <ArrowRight weight="bold" size={18} />
          </button>
          <a
            data-testid="exec-call-btn"
            href={`tel:${PHONE_RAW}`}
            className="flex items-center gap-2.5 text-white font-bold text-base px-7 py-4 border-2 border-white/40 hover:bg-white hover:text-ink transition-colors"
          >
            <Phone weight="fill" size={18} /> {PHONE}
          </a>
        </div>
      </div>

      {/* Van illustration */}
      <div className="relative flex flex-col items-center justify-center">
        <motion.img
          src="/exec-van.png?v=5"
          alt="DS Executive van"
          data-testid="exec-van-illustration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, -12, 0] }}
          transition={{ opacity: { duration: 0.8 }, y: { duration: 4.5, repeat: Infinity, ease: "easeInOut" } }}
          className="relative z-10 w-full max-w-[580px]"
        />
        <p className="mt-4 text-xs uppercase tracking-[0.25em] text-white/40">{EXECUTIVE.vehicle}</p>
      </div>
    </div>
  </section>
);
